/**
 * Watchtower Calendar Export — .ics generator
 *
 * Pure module: same inputs → same output (modulo `DTSTAMP`, which the `ics`
 * package fills with the current time and we cannot override). Implements
 * the locked contract from `docs/architecture/watchtower-calendar-export.md`
 * §3 (Generation Module). All design decisions are referenced inline.
 *
 * Imported only by `icsGenerator.test.ts` and `hipaa.test.ts` in this PR;
 * production wiring lands in PR-3 (modal/hook) and PR-4 (UI).
 *
 * IMPORTANT (whitelist contract): the generator reads ONLY the named
 * `Deadline` fields it serializes. Adding fields to `Deadline` (e.g. a
 * future `authorizationReviewData` or `primaryDiagnosis`) MUST NOT cause
 * leakage — that is the explicit forward-protection contract per §7 of the
 * design doc and is enforced by `hipaa.test.ts`.
 */

import { createEvent, type EventAttributes, type Alarm } from 'ics';
import {
  DEADLINE_PRIORITIES,
  DEADLINE_TYPES,
  type Deadline,
} from '../../components/deadlines/types';
import {
  HESVARA_EPOCH,
  HESVARA_PRODID,
  HESVARA_UID_DOMAIN,
  ICS_PRIORITY_MAP,
  type CalendarExportOptions,
  type CalendarExportResult,
} from './types';

const PRIORITY_LABELS: Record<string, string> = Object.fromEntries(
  DEADLINE_PRIORITIES.map((p) => [p.value, p.label]),
);

const TYPE_LABELS: Record<string, string> = Object.fromEntries(
  DEADLINE_TYPES.map((t) => [t.value, t.label]),
);

/**
 * Parse an ISO date string (`YYYY-MM-DD`) into `[year, month, day]` without
 * touching the JS `Date` constructor — the latter would inject local-tz
 * drift on UTC-boundary dates (Bug Fix #3 in CLAUDE.md). Floating all-day
 * events have no TZID; numeric splitting is the only correct approach.
 */
const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

function parseIsoDateToTuple(iso: string): [number, number, number] {
  // Guard 1: runtime type. TS strict declares `iso: string`, but Firestore
  // deserialization, callable payloads, and other untyped boundaries can
  // deliver `null`/`undefined`/non-string values at runtime. Reject with
  // the same error shape as the round-trip check so PR-3 (modal) sees a
  // single uniform failure mode. See tasks/TASK-013.md "Adversarial
  // review (second pass) — d48d2fa" finding 1.
  if (typeof iso !== 'string') {
    throw new Error(`Invalid calendar date: ${iso}`);
  }
  // Guard 2: shape contract. The function name claims `YYYY-MM-DD`, and
  // RFC 5545 DATE values require a 4-digit year. Enforce shape before
  // touching `.split('-')` so width-invalid years (`10000-01-01`),
  // 1-digit months/days, wrong separators, and embedded whitespace are
  // rejected at the contract boundary rather than slipping past the
  // range/round-trip checks. Do NOT trim the input — trimming would mask
  // data corruption upstream. See tasks/TASK-013.md "Adversarial review
  // (second pass) — d48d2fa" finding 2.
  if (!ISO_DATE_SHAPE.test(iso)) {
    throw new Error(`Invalid calendar date: ${iso}`);
  }
  const parts = iso.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    throw new Error(`Invalid date values in: ${iso}`);
  }
  // Trust-boundary failsafe: per-field range checks accept impossible
  // calendar dates (Feb 29 in non-leap years, day 31 in 30-day months).
  // Date.UTC normalizes overflow (e.g. month=4, day=31 → May 1), so a
  // round-trip mismatch on the constructed components reveals the bad
  // input. The generator does not normalize: PR-3 (modal) is responsible
  // for catching the throw and surfacing the error before re-submission.
  // See tasks/TASK-013.md "Adversarial review — c8e3f82" finding 2.
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    throw new Error(`Invalid calendar date: ${iso}`);
  }
  return [year, month, day];
}

/**
 * SEQUENCE = floor(now-seconds) - HESVARA_EPOCH. Monotonic at second-level
 * resolution, fits in a signed 32-bit int for ~68 years. Re-export of the
 * same UID with a higher SEQUENCE causes calendar apps to update the
 * existing event rather than duplicate it. Per §3 + §10 risk #3.
 */
function computeSequence(nowMs: number = Date.now()): number {
  return Math.floor(nowMs / 1000) - HESVARA_EPOCH;
}

/**
 * Build the PHI-safe default title. Per §3 default-title table.
 *
 * - `includeRecipientName=false` (default): `Hesvara Deadline: {title}`
 * - `includeRecipientName=true`:           `Hesvara Deadline ({name}): {title}`
 */
function buildDefaultTitle(
  deadline: Deadline,
  recipientName: string | undefined,
  includeRecipientName: boolean,
): string {
  if (includeRecipientName && recipientName) {
    return `Hesvara Deadline (${recipientName}): ${deadline.title}`;
  }
  return `Hesvara Deadline: ${deadline.title}`;
}

/**
 * Build the PHI-safe default description. Per §3 default-description table.
 * The `\n` separators are JS literals; the `ics` package handles RFC 5545
 * escaping and emits them as `\\n` on the wire.
 */
function buildDefaultDescription(
  deadline: Deadline,
  recipientName: string | undefined,
  includeRecipientName: boolean,
): string {
  const typeLabel = TYPE_LABELS[deadline.type] ?? deadline.type;
  const priorityLabel = PRIORITY_LABELS[deadline.priority] ?? deadline.priority;
  // Em-dash (`—`, 3 UTF-8 octets) is safe here because the post-process
  // `foldTextPropertiesToOctets` re-folds every TEXT property at octet
  // boundaries; the previous ASCII `--` mitigation was symptom-specific
  // and offered no protection for user-supplied custom titles/descriptions.
  // See tasks/TASK-013.md "Adversarial review — c8e3f82" finding 1.
  const base =
    `Type: ${typeLabel}\n` +
    `Priority: ${priorityLabel}\n\n` +
    `Managed in Hesvara — open the app for full context.`;
  if (includeRecipientName && recipientName) {
    return `${base}\nFor: ${recipientName}`;
  }
  return base;
}

/**
 * `ics@3.12.0` does not natively emit `PRIORITY:` lines (no field on
 * `EventAttributes`, no source references). The architecture doc §3 + the
 * acceptance criteria require it, so we inject the line into the VEVENT
 * block as a post-processing step. RFC 5545 §3.6 does not mandate property
 * ordering within a component; insertion immediately after `UID:` is
 * stable, valid, and easy to assert in tests.
 */
function injectPriorityLine(ics: string, priority: number): string {
  // VEVENT must already contain a UID line per the deterministic-UID
  // contract. Insert the new line right after it.
  const uidLineMatch = ics.match(/UID:[^\r\n]+\r?\n/);
  if (!uidLineMatch) {
    throw new Error('Internal: generated .ics is missing UID line');
  }
  const insertAt = (uidLineMatch.index ?? 0) + uidLineMatch[0].length;
  // Use CRLF to match the surrounding line endings emitted by `ics`.
  const newline = uidLineMatch[0].endsWith('\r\n') ? '\r\n' : '\n';
  return ics.slice(0, insertAt) + `PRIORITY:${priority}` + newline + ics.slice(insertAt);
}

/**
 * Workaround for `ics@3.12.0`: pure-day durations are emitted as `P1DT`
 * (and `-P7DT`, `-P1DT` for triggers) — the trailing `T` is the
 * date/time separator with no time component after it, which is invalid
 * ISO 8601 and contradicts RFC 5545 §3.3.6. Spec Appendix A explicitly
 * shows the conformant `P1D` / `-P7D` / `-P1D` form. Strip the stray `T`
 * on `DURATION:` and `TRIGGER:` lines whose value ends in `D` followed by
 * `T` and a line break.
 *
 * Track for upstream report: github.com/adamgibbons/ics — pure-day
 * `DurationObject` should not append `T` if no h/m/s are set.
 */
function normalizeDurations(ics: string): string {
  return ics.replace(/^(DURATION|TRIGGER):(-?P\d+D)T(\r?\n)/gm, '$1:$2$3');
}

const TEXT_ENCODER = new TextEncoder();

function octetLength(s: string): number {
  return TEXT_ENCODER.encode(s).length;
}

/**
 * Re-fold every logical line in the .ics output to ≤75 octets per RFC 5545
 * §3.1. `ics@3.12.0` folds at JavaScript character boundaries, not UTF-8
 * octets, so any line with multibyte content (em-dash, accented Latin,
 * emoji, CJK) can ship overlong. This pass is the only TEXT-property octet
 * guarantee — `customTitle` and `customDescription` flow straight through
 * `createEvent()` and would otherwise bypass the (now-removed) ASCII-only
 * mitigation that previously sat in `buildDefaultDescription`.
 *
 * Algorithm:
 *  1. Split on physical line breaks; rejoin any continuation lines
 *     (CRLF + SP/HTAB) back into their parent logical line. The leading
 *     whitespace of the continuation is fold metadata, not content.
 *  2. For each logical line, if it exceeds 75 octets, re-fold at codepoint
 *     boundaries — never splitting a multibyte sequence mid-byte. The first
 *     segment uses the full 75-octet budget; continuations lose 1 octet to
 *     the leading SP, leaving 74 octets for content.
 *  3. Emit with CRLF + single space continuation per §3.1.
 *
 * Codepoint atomicity: `for...of` on a string iterates Unicode codepoints
 * (yielding correctly-paired surrogates for non-BMP characters), so a
 * single iteration step yields a string of 1–4 UTF-8 octets — well under
 * the 74-octet continuation budget.
 *
 * Removable when upstream `adamgibbons/ics` folds at byte boundaries.
 */
function foldTextPropertiesToOctets(ics: string): string {
  const physical = ics.split(/\r?\n/);

  const logical: string[] = [];
  for (const line of physical) {
    const isContinuation =
      line.length > 0 && (line[0] === ' ' || line[0] === '\t') && logical.length > 0;
    if (isContinuation) {
      logical[logical.length - 1] += line.slice(1);
    } else {
      logical.push(line);
    }
  }

  const refolded = logical.map((line) => foldLineByOctets(line, 75));
  return refolded.join('\r\n');
}

function foldLineByOctets(line: string, maxOctets: number): string {
  if (octetLength(line) <= maxOctets) return line;

  const segments: string[] = [];
  let current = '';
  let currentOctets = 0;
  let isFirst = true;

  for (const codepoint of line) {
    const cpOctets = octetLength(codepoint);
    const limit = isFirst ? maxOctets : maxOctets - 1;
    if (currentOctets + cpOctets > limit) {
      segments.push(current);
      current = codepoint;
      currentOctets = cpOctets;
      isFirst = false;
    } else {
      current += codepoint;
      currentOctets += cpOctets;
    }
  }
  if (current.length > 0) segments.push(current);

  return segments[0] + segments.slice(1).map((s) => '\r\n ' + s).join('');
}

/**
 * Trimmed-equality check for the diff flags. If the user types the default
 * back in (with whitespace differences), it does not count as "modified" —
 * that distinction is consumed by the audit endpoint in PR-2.
 */
function valueDiffersFromDefault(
  value: string | undefined,
  defaultValue: string,
): boolean {
  if (value === undefined) return false;
  return value.trim() !== defaultValue.trim();
}

/**
 * Generate an .ics document for a single deadline.
 *
 * @see docs/architecture/watchtower-calendar-export.md §3
 */
export function buildDeadlineIcs(
  deadline: Deadline,
  recipientName: string | undefined,
  options: CalendarExportOptions,
): CalendarExportResult {
  const includeRecipientName = options.includeRecipientName === true;

  const defaultTitle = buildDefaultTitle(deadline, recipientName, includeRecipientName);
  const defaultDescription = buildDefaultDescription(
    deadline,
    recipientName,
    includeRecipientName,
  );

  const titleModified = valueDiffersFromDefault(options.customTitle, defaultTitle);
  const descriptionModified = valueDiffersFromDefault(
    options.customDescription,
    defaultDescription,
  );

  const finalTitle = options.customTitle !== undefined ? options.customTitle : defaultTitle;
  const finalDescription =
    options.customDescription !== undefined ? options.customDescription : defaultDescription;

  const startTuple = parseIsoDateToTuple(deadline.dueDate);
  const priority = ICS_PRIORITY_MAP[deadline.priority];

  const alarms: Alarm[] = [
    {
      action: 'display',
      trigger: { days: 7, before: true },
      description: 'Hesvara deadline reminder (7 days)',
    },
    {
      action: 'display',
      trigger: { days: 1, before: true },
      description: 'Hesvara deadline reminder (1 day)',
    },
  ];

  const event: EventAttributes = {
    start: startTuple,
    duration: { days: 1 }, // §3: all-day end via 1-day DURATION
    title: finalTitle,
    description: finalDescription,
    uid: `watchtower-${deadline.deadlineId}@${HESVARA_UID_DOMAIN}`,
    productId: HESVARA_PRODID,
    sequence: computeSequence(),
    alarms,
    // Note: NO recurrenceRule. Tier 1 exports the next occurrence only —
    // `deadline.dueDate` already points at the next occurrence per
    // Watchtower's recurring-deadline model.
  };

  const { error, value } = createEvent(event);
  if (error || !value) {
    throw new Error(`Failed to build .ics: ${error?.message ?? 'unknown error'}`);
  }

  // Post-processing pipeline (order-sensitive):
  //   1. injectPriorityLine — adds locked PRIORITY: line after UID
  //   2. normalizeDurations — strips stray `T` from pure-day durations
  //   3. foldTextPropertiesToOctets — unfolds and re-folds at octet
  //      boundaries; runs LAST so it sees final wire content for every
  //      property (including any lines added by step 1)
  const ics = foldTextPropertiesToOctets(
    normalizeDurations(injectPriorityLine(value, priority)),
  );

  // Opaque filename per §3 + risk #4. NEVER derived from the title or the
  // recipient name — surfaces visible before the file is opened (download
  // bars, OS notifications, shared-folder previews) must not leak PHI.
  const shortId = deadline.deadlineId.slice(0, 8);
  const filename = `hesvara-deadline-${shortId}.ics`;

  return {
    ics,
    filename,
    diff: {
      titleModified,
      descriptionModified,
      includedRecipientName: includeRecipientName,
    },
  };
}
